import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import type { GetGuestMembersForContactPickerQuery, GetGuestMembersForContactPickerQueryVariables } from './ContactPicker.graphql.generated';
import { GetGuestMembersForContactPickerDocument, useGetGuestMembersForContactPickerQuery } from './ContactPicker.graphql.generated';
import { FETCH_ALL_LIMIT } from './contactPicker.constants';
import { addError } from '@shared/utils/logger';

type Connection = NonNullable<GetGuestMembersForContactPickerQuery['getGuestListMembersByEventId']>;
export type GuestListMember = Connection['edges'][number]['node'];

/**
 * Every guest-list member for an event, following `pageInfo.endCursor` until the connection is
 * exhausted. `FETCH_ALL_LIMIT` is the page size, not a ceiling: the picker and the draft-resume
 * lookup both read through this hook so they agree on which guests exist (MGT-76).
 *
 * `loading` stays true while later pages are in flight; `complete` is the only signal that the list
 * is the whole roster — an error or a page still to come is an absent answer, not an empty one.
 */
export const useGuestListMembers = (eventId: string | undefined, options: Readonly<{ skip?: boolean }> = {}) => {
  const { skip = false } = options;
  const { data, error, loading, fetchMore, refetch, client } = useGetGuestMembersForContactPickerQuery({
    variables: { eventId: eventId ?? '', first: FETCH_ALL_LIMIT },
    skip: skip || !eventId,
    batchMode: 'fast',
  });

  const connection = data?.getGuestListMembersByEventId;
  const hasNextPage = !!connection?.pageInfo?.hasNextPage;
  const endCursor = connection?.pageInfo?.endCursor ?? null;

  // A rejected fetchMore never reaches Apollo's `error`; without tracking it here the hook would sit in `loading` forever.
  const [fetchMoreError, setFetchMoreError] = useState<ApolloError | undefined>(undefined);
  // A re-render between the request and the cache update must not ask for the same cursor again — keyed by event, since cursors are per event.
  const requestedRef = useRef<{ eventId: string; cursor: string } | null>(null);
  useEffect(() => {
    if (!eventId || skip || error || fetchMoreError || !hasNextPage || !endCursor || typeof fetchMore !== 'function') return;
    if (requestedRef.current?.eventId === eventId && requestedRef.current.cursor === endCursor) return;
    requestedRef.current = { eventId, cursor: endCursor };
    fetchMore({
      variables: { eventId, first: FETCH_ALL_LIMIT, after: endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        const next = fetchMoreResult?.getGuestListMembersByEventId;
        // A roster already complete has nothing left to append: a page requested before `refresh`
        // wrote the whole roster would otherwise land stale guests and a stale cursor on top of it.
        if (!next || prev.getGuestListMembersByEventId?.pageInfo?.hasNextPage === false) return prev;
        // Idempotent: every mounted instance's updateQuery runs on the one shared response.
        const prevEdges = prev.getGuestListMembersByEventId?.edges ?? [];
        const seen = new Set(prevEdges.map(edge => edge.node.id));
        return {
          ...prev,
          getGuestListMembersByEventId: { ...next, edges: [...prevEdges, ...next.edges.filter(edge => !seen.has(edge.node.id))] },
        };
      },
    }).catch((caught: unknown) => {
      // Same shape as the first page's `error`, so consumers need only one branch.
      setFetchMoreError(caught instanceof ApolloError ? caught : new ApolloError({ networkError: caught instanceof Error ? caught : new Error(String(caught)) }));
    });
  }, [eventId, skip, error, fetchMoreError, hasNextPage, endCursor, fetchMore]);

  const members = useMemo<Array<GuestListMember>>(() => connection?.edges?.map(edge => edge.node) ?? [], [connection]);
  const anyError = error ?? fetchMoreError;

  // Opt-in recovery: clears a failed later page so paging resumes, and refetches when the first page failed.
  const retry = useCallback(async (): Promise<void> => {
    requestedRef.current = null;
    setFetchMoreError(undefined);
    if (error && typeof refetch === 'function') await refetch().catch(() => undefined);
  }, [error, refetch]);

  // Background re-read of the whole roster, for edits made outside this tab (MSG-568). Every page is
  // fetched before anything is written, then written as the one connection the query above reads, so
  // a long roster never drops back to its first page while the rest re-pages. A failure leaves the
  // cached roster in place, and a newer refresh supersedes an older one still in flight.
  const refreshGenerationRef = useRef(0);
  const refresh = useCallback(async (): Promise<void> => {
    if (!eventId || skip) return;
    const generation = ++refreshGenerationRef.current;
    try {
      const edges: Connection['edges'] = [];
      const seen = new Set<string>();
      let after: string | null = null;
      let page: Connection | null | undefined;
      do {
        const result: { data: GetGuestMembersForContactPickerQuery } = await client.query<GetGuestMembersForContactPickerQuery, GetGuestMembersForContactPickerQueryVariables>({
          query: GetGuestMembersForContactPickerDocument,
          variables: { eventId, first: FETCH_ALL_LIMIT, ...(after ? { after } : null) },
          // Pages are stitched here, so none of them is cached under its own cursor.
          fetchPolicy: 'no-cache',
        });
        page = result.data.getGuestListMembersByEventId;
        for (const edge of page?.edges ?? []) {
          if (!seen.has(edge.node.id)) {
            seen.add(edge.node.id);
            edges.push(edge);
          }
        }
        after = page?.pageInfo?.hasNextPage ? (page.pageInfo.endCursor ?? null) : null;
      } while (after);
      if (!page || generation !== refreshGenerationRef.current) return;
      client.writeQuery<GetGuestMembersForContactPickerQuery, GetGuestMembersForContactPickerQueryVariables>({
        query: GetGuestMembersForContactPickerDocument,
        variables: { eventId, first: FETCH_ALL_LIMIT },
        data: { getGuestListMembersByEventId: { ...page, edges } },
      });
    } catch (caught) {
      // The roster already on screen stays; a failed background read is only reported.
      addError(caught, { context: 'useGuestListMembers.refresh', eventId });
    }
  }, [client, eventId, skip]);

  return {
    data,
    members,
    error: anyError,
    loading: loading || (hasNextPage && !anyError),
    complete: !!connection && !hasNextPage && !anyError,
    retry,
    refresh,
  };
};
